import React, { createContext, useContext, useState, useCallback } from 'react';
import { Snackbar } from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import { makeStyles } from '@material-ui/core/styles';
import { designTokens } from '../styles/designTokens';

const useStyles = makeStyles({
    alert: {
        borderRadius: designTokens.borderRadius.button,
        fontFamily: designTokens.typography.fontFamily,
        fontSize: '14px',
        fontWeight: 500,
    },
});

const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
    const classes = useStyles();
    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'success',
    });

    const showNotification = useCallback((message, severity = 'success') => {
        setNotification({
            open: true,
            message,
            severity,
        });
    }, []);

    const showSuccess = useCallback((message) => {
        showNotification(message, 'success');
    }, [showNotification]);

    const showError = useCallback((message) => {
        showNotification(message, 'error');
    }, [showNotification]);

    const showWarning = useCallback((message) => {
        showNotification(message, 'warning');
    }, [showNotification]);

    const showInfo = useCallback((message) => {
        showNotification(message, 'info');
    }, [showNotification]);

    const hideNotification = useCallback(() => {
        setNotification((prev) => ({ ...prev, open: false }));
    }, []);

    const handleClose = (event, reason) => {
        if (reason === 'clickaway') return;
        hideNotification();
    };

    const value = {
        showNotification,
        showSuccess,
        showError,
        showWarning,
        showInfo,
        hideNotification,
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
            <Snackbar
                open={notification.open}
                autoHideDuration={4000}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <MuiAlert
                    className={classes.alert}
                    elevation={6}
                    variant="filled"
                    onClose={handleClose}
                    severity={notification.severity}
                >
                    {notification.message}
                </MuiAlert>
            </Snackbar>
        </NotificationContext.Provider>
    );
}

export function useNotification() {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotification must be used within a NotificationProvider');
    }
    return context;
}

export default NotificationContext;
